import React, { useState, useEffect, useMemo } from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Divider,
    Chip
} from "@mui/material"

/**
 * Lets the user pick any number of class/exam pairs and apply a copied
 * exam mark configuration (total mark, pass mark, grading rows) to all of them.
 */
export default function ApplyConfigDialog({
    open,
    onClose,
    config,
    classList = [],
    examList = [],
    onApply,
    applying = false
}) {
    const [selectedExamIds, setSelectedExamIds] = useState([])

    useEffect(() => {
        if (open) setSelectedExamIds([])
    }, [open])

    // Group exams under their class, skipping the exam the config was copied from
    const groups = useMemo(() => {
        return classList.map((cls) => ({
            cls,
            exams: examList.filter((exam) =>
                exam.school_ref === cls.institution_ref &&
                exam.class_ref === cls.id &&
                exam.id !== config?.sourceExamId
            )
        })).filter(group => group.exams.length > 0)
    }, [classList, examList, config])

    function toggleExam(examId) {
        setSelectedExamIds(prev => prev.includes(examId) ? prev.filter(id => id !== examId) : [...prev, examId])
    }

    function toggleClass(exams, checked) {
        const ids = exams.map(exam => exam.id)
        setSelectedExamIds(prev => checked
            ? [...new Set([...prev, ...ids])]
            : prev.filter(id => !ids.includes(id)))
    }

    function handleApply() {
        const targets = examList.filter(exam => selectedExamIds.includes(exam.id))
        onApply(targets)
    }

    if (!config) return null

    return (
        <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontFamily: "Lato", fontWeight: "bold" }}>
                Apply Configuration to Classes / Exams
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: "Lato", fontSize: "14px" }}>
                        Copied from: <b>{config.sourceLabel}</b>
                    </Typography>
                    <Typography sx={{ fontFamily: "Lato", fontSize: "14px" }}>
                        Total Marks: <b>{config.total_mark}</b> &nbsp;|&nbsp; Pass Marks: <b>{config.pass_marks}</b>
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: 1 }}>
                        {config.dynamicForm.map((field, index) => (
                            <Chip
                                key={index}
                                size="small"
                                label={`${field.label}: ${field.value}${field.remark ? ` (${field.remark})` : ""}`}
                            />
                        ))}
                    </Box>
                </Box>
                <Divider />
                {
                    groups.length === 0 ? (
                        <Typography sx={{ fontFamily: "Lato", mt: 2 }}>
                            No other exams available.
                        </Typography>
                    ) : groups.map(({ cls, exams }) => {
                        const selectedCount = exams.filter(exam => selectedExamIds.includes(exam.id)).length
                        return (
                            <Box key={cls.id} sx={{ mt: 1 }}>
                                <FormControlLabel
                                    label={<Typography sx={{ fontFamily: "Lato", fontWeight: "bold" }}>{cls.class_name}</Typography>}
                                    control={
                                        <Checkbox
                                            checked={selectedCount === exams.length}
                                            indeterminate={selectedCount > 0 && selectedCount < exams.length}
                                            onChange={(e) => toggleClass(exams, e.target.checked)}
                                        />
                                    }
                                />
                                <Box sx={{ display: "flex", flexDirection: "column", pl: 4 }}>
                                    {exams.map(exam => (
                                        <FormControlLabel
                                            key={exam.id}
                                            label={exam.exam_name}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedExamIds.includes(exam.id)}
                                                    onChange={() => toggleExam(exam.id)}
                                                />
                                            }
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )
                    })
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={applying}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleApply}
                    disabled={applying || selectedExamIds.length === 0}
                >
                    {applying ? "Applying..." : `Apply to ${selectedExamIds.length} Exam(s)`}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
